import { onboardingRefreshInterceptor } from './core/interceptors/onboarding-refresh.interceptor';
import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { AuthCookieInterceptor } from './core/interceptors/auth-cookie.interceptor';
import { acceptProblemDetailInterceptor } from './core/http/accept-problem-detail.interceptor';
import { acceptLanguageInterceptor } from './core/http/accept-language.interceptor';
import { ifMatchInterceptor } from './core/interceptors/if-match.interceptor';
import { httpErrorInterceptor } from './core/http/http-error.interceptor';
import { firstValueFrom } from 'rxjs';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { SUPPORTED_LANGUAGE_IDS, isSupportedLanguage, supportedLanguage } from './core/i18n/supported-languages';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideCharts(withDefaultRegisterables()),

    // Order matters: function interceptors first, then DI interceptors.
    provideHttpClient(
      withInterceptors([onboardingRefreshInterceptor, acceptLanguageInterceptor, acceptProblemDetailInterceptor, ifMatchInterceptor, httpErrorInterceptor]),
      withInterceptorsFromDi(),
    ),

    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGUAGE_IDS],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    provideAppInitializer(() => initializeI18n()),

    // Bearer retry runs outside cookie serialization; nested refresh must acquire a fresh lock.
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthCookieInterceptor, multi: true },
  ]
};

async function initializeI18n(): Promise<void> {
  const transloco = inject(TranslocoService);
  const savedLang = typeof window !== 'undefined'
    ? window.localStorage.getItem('speed.lang')
    : null;
  const selectedLang = isSupportedLanguage(savedLang) ? savedLang : 'en';

  transloco.setActiveLang(selectedLang);
  await firstValueFrom(transloco.load(selectedLang));
  if (typeof document !== 'undefined') {
    document.documentElement.lang = supportedLanguage(selectedLang).htmlLang;
  }
}
